/**
 * The doctor card, and everything it is made of.
 *
 * It lives in its own file because two screens show it: the doctor directory
 * and the telemedicine screen, which in the Patient app is that same directory
 * with the Telemedicine filter already on. Rendering the card from one place
 * is what keeps those two from drifting apart.
 */
import { useState } from 'react';
import { T } from '@design/index';
import { apiFileUrl } from '../../../../api/http';
import type { MarketplaceProvider, ProviderPricing } from '../../types';
import { distanceLabel, type Coords } from '../../utils/patientLocation';

/**
 * A 12-digit NeoHealthCard number as "XXXX XXXX XXXX", or null when the
 * directory carries no issued id for this provider. Anything that is not
 * exactly twelve digits is not an id.
 */
function formatNhcId(raw: unknown): string | null {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length !== 12) return null;
  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
}

// ─── Inline SVG Icons ────────────────────────────────────────────────────────

function IconStar({ filled, color }: { filled: boolean; color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function IconLocation({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconVerified({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M12 2l2.5 4.5L19 7.5l-3.5 3.5.8 5L12 14l-4.3 2 .8-5L5 7.5l4.5-1L12 2z" />
    </svg>
  );
}

export function IconX({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function IconChevronRight({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function IconHeart({ filled, color }: { filled: boolean; color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.7-7.7 1.1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

function IconSchool({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10L12 5 2 10l10 5 10-5z" />
      <path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
    </svg>
  );
}

function IconBuilding({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M15 21V11h4a2 2 0 0 1 2 2v8" />
      <path d="M9 7h2M9 11h2M9 15h2" />
    </svg>
  );
}

function IconChat({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.8-.9L3 20.5l1.6-4.9A8.4 8.4 0 0 1 3.5 11 8.4 8.4 0 0 1 12 2.6a8.4 8.4 0 0 1 9 8.9z" />
    </svg>
  );
}

function IconInfo({ color }: { color: string }) {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

// ─── Star Rating ─────────────────────────────────────────────────────────────

/**
 * Aggregate-rating chip — amber star + score + review count, or an honest
 * "New · not yet rated" when the directory holds no rating for this doctor.
 * Showing nothing at all read as "we have no opinion"; every doctor in the
 * network is currently unrated, so every card was silently missing this row.
 */
function RatingChip({ rating, count }: { rating?: number; count?: number }) {
  if (!rating) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '4px 10px', borderRadius: 999,
        background: T.textSec + '12', border: `1px solid ${T.textSec}29`,
        fontFamily: T.font, fontSize: 10.5, fontWeight: 800, color: T.textSec,
      }}>
        New · not yet rated
      </span>
    );
  }
  const star = '#F59E0B';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '4px 10px', borderRadius: 999,
      background: star + '14', border: `1px solid ${star}29`,
      fontFamily: T.font, fontSize: 10.5, fontWeight: 800, color: T.text,
    }}>
      <IconStar filled color={star} />
      {count != null ? `${rating.toFixed(1)} · (${count} ratings)` : rating.toFixed(1)}
    </span>
  );
}

/** Tinted pill chip — the app's `DocChip`. */
function Chip({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '4px 10px', borderRadius: 999,
      background: color + '14', border: `1px solid ${color}29`,
      fontFamily: T.font, fontSize: 10.5, fontWeight: 800, color,
    }}>
      {label}
    </span>
  );
}

/** Small squared consultation-mode tag — the app's `DocModePill`. */
function ModePill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 7, background: color + '10',
      fontFamily: T.font, fontSize: 10, fontWeight: 700, color,
    }}>
      {label}
    </span>
  );
}

/**
 * Square initials avatar with the doctor's own photo when they have published
 * one. The photo comes from `doctor.photoUrl` and has to be resolved against
 * the API host — as a bare path it is answered by this site's index.html.
 */
function DoctorAvatar({ name, photoUrl, size = 52 }: { name: string; photoUrl?: string | null; size?: number }) {
  const [broken, setBroken] = useState(false);
  const src = apiFileUrl(photoUrl);
  const initials = name
    .replace(/^Dr\.?\s+/i, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  return (
    <div style={{
      width: size, height: size, flexShrink: 0, overflow: 'hidden',
      borderRadius: size * 0.28,
      background: `linear-gradient(135deg, ${T.teal}21 0%, ${T.navy}21 100%)`,
      border: `1.5px solid ${T.teal}33`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxSizing: 'border-box',
    }}>
      {/* `crossOrigin` matters: the API serves photos with Helmet's
          `Cross-Origin-Resource-Policy: same-site`, which blocks a plain
          cross-origin <img> — silently, straight to the initials. Asking for
          it as a CORS request satisfies both that header and the allow-list. */}
      {src && !broken
        ? <img src={src} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setBroken(true)} />
        : <span style={{ fontFamily: T.font, fontSize: size * 0.34, fontWeight: 800, color: T.navy }}>{initials || '?'}</span>}
    </div>
  );
}

/**
 * Does this doctor actually consult online?
 *
 * The answer is `consultModes` — what the server derived from the schedules
 * the doctor published — or the same list on their own profile. It is NOT
 * `tags`, which is a free-text label a provider typed: filtering on that hid
 * two of the six doctors who do offer video consultations (one of them had no
 * tags at all) while their published video fees sat unread.
 */
export function offersOnline(p: MarketplaceProvider): boolean {
  const modes = p.doctor?.consultation.modes?.length ? p.doctor.consultation.modes : (p.consultModes ?? []);
  // An empty list means the doctor published no schedule, so nothing is known
  // — not "clinic only". They stay in, the same way the services card offers
  // both channels for them and the booking step shows what really exists.
  return modes.length === 0 || modes.includes('ONLINE');
}

const MODE_LABEL: Record<string, { label: string; color: string }> = {
  IN_CLINIC: { label: 'Clinic', color: T.navy },
  ONLINE: { label: 'Telemedicine', color: T.info },
  HOME_VISIT: { label: 'Home visit', color: T.success },
};

/**
 * The practical questions a patient asks before booking — platform policy,
 * answered from this doctor's own published fees and modes. Deliberately not
 * a message to the doctor: nothing here reaches them, and it says so.
 */
function QuickQuestionsSheet({ provider, onClose, onViewProfile }: { provider: MarketplaceProvider; onClose: () => void; onViewProfile: () => void }) {
  const dp = provider.doctor ?? null;
  const modes = dp?.consultation.modes ?? provider.consultModes ?? [];
  const offersVideo = modes.includes('ONLINE');
  const clinicFee = dp?.consultation.clinicFee ?? null;

  const faqs: [string, string][] = [
    [
      'What should I bring to my visit?',
      'Previous reports and your current medicines list. You can also share records securely from NeoHealthCard once the appointment is booked.',
    ],
    [
      'How early should I arrive?',
      'Arrive about 10 minutes before your slot for check-in. The clinic issues your queue token at the desk or via QR.',
    ],
    [
      'How are consultation fees handled?',
      clinicFee
        ? `The clinic consultation is ₹${clinicFee}, settled at the clinic. NeoHealthCard does not take an online consultation payment for doctors.`
        : 'Fees are settled at the clinic. NeoHealthCard does not take an online consultation payment for doctors.',
    ],
    offersVideo
      ? ['Can I consult online instead?', `Yes — book the video consultation to see ${provider.name} securely from home.`]
      : ['Can I consult online instead?', `${provider.name} has not published online consultations, so appointments are in clinic.`],
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 120,
        background: 'rgba(11,37,69,0.45)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560, maxHeight: '82vh', overflowY: 'auto',
          background: T.white, borderRadius: '18px 18px 0 0', padding: '10px 16px 22px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ width: 38, height: 4, borderRadius: 2, background: T.border, margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 700, color: T.text }}>Quick questions · {provider.name}</div>
            {provider.specialties?.[0] && (
              <div style={{ fontFamily: T.font, fontSize: 12, color: T.textTer, marginTop: 1 }}>{provider.specialties[0]}</div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
            <IconX color={T.textTer} />
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          margin: '12px 0', padding: 11, borderRadius: 11,
          background: T.info + '0D', border: `1px solid ${T.info}30`,
        }}>
          <span style={{ flexShrink: 0, display: 'flex', paddingTop: 1 }}><IconInfo color={T.info} /></span>
          <span style={{ fontFamily: T.font, fontSize: 11, color: T.text, lineHeight: 1.5 }}>
            For frequent, practical questions — timings, location, documents, fees. Not for symptoms,
            complaints, or medical advice. For anything clinical, please book a consultation.
          </span>
        </div>

        <div style={{ fontFamily: T.font, fontSize: 11, fontWeight: 800, letterSpacing: '0.03em', color: T.text, marginBottom: 8 }}>
          COMMON QUESTIONS
        </div>
        {faqs.map(([q, a]) => (
          <div key={q} style={{ marginBottom: 8, padding: 12, borderRadius: 12, background: T.ice, border: `1px solid ${T.border}` }}>
            <div style={{ fontFamily: T.font, fontSize: 12.5, fontWeight: 700, color: T.text }}>{q}</div>
            <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, lineHeight: 1.55, marginTop: 4 }}>{a}</div>
          </div>
        ))}

        <button
          onClick={() => { onClose(); onViewProfile(); }}
          style={{
            width: '100%', marginTop: 4, padding: '12px 0', borderRadius: 10,
            background: T.teal + '1A', border: `1px solid ${T.teal}47`, cursor: 'pointer',
            fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.navy,
          }}
        >
          View full profile
        </button>
      </div>
    </div>
  );
}

// ─── Doctor Card ─────────────────────────────────────────────────────────────

export function DoctorCard({ provider, coords, isSaved, channel = 'IN_CLINIC', onToggleSave, onViewProfile, onBook }: {
  provider: MarketplaceProvider;
  coords: Coords | null;
  isSaved: boolean;
  /**
   * Which consultation this card is offering. The telemedicine screen books a
   * video consultation, so it must quote the video fee — quoting the clinic
   * price beside a button that books a video call is a different number for a
   * different thing.
   */
  channel?: 'IN_CLINIC' | 'ONLINE';
  onToggleSave: () => void;
  onViewProfile: () => void;
  onBook: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [askOpen, setAskOpen] = useState(false);

  const dp = provider.doctor ?? null;

  const priceList: ProviderPricing[] = (() => {
    if (!provider.pricing) return [];
    const list = typeof provider.pricing === 'string' ? JSON.parse(provider.pricing) : provider.pricing;
    return Array.isArray(list) ? list : [];
  })();
  // The doctor's own published price for the consultation this card offers.
  // The directory's `pricing` rows come first; a doctor who has only filled in
  // their profile fee still has one, and it used to go unshown.
  const priceNamed = (service: string) =>
    priceList.find((r) => (r.service ?? '').toLowerCase() === service)?.price ?? null;
  const online = channel === 'ONLINE';
  const consultFee = online
    ? (priceNamed('telemedicine') ?? dp?.consultation.videoFee ?? priceNamed('consultation') ?? dp?.consultation.clinicFee ?? null)
    : (priceNamed('consultation') ?? dp?.consultation.clinicFee ?? priceList[0]?.price ?? null);
  const feeLabel = online ? 'Video consultation from' : 'Consultation from';

  // Experience, degrees and languages are the doctor's own record. The card
  // used to look for `metadata.experience`, a key this API does not send, so
  // it was blank for every doctor while the real figure sat in `doctor`.
  const experienceLabel = dp?.experienceYears ? `${dp.experienceYears} years experience` : null;
  const degreesLine = dp?.qualifications?.length ? dp.qualifications.join(' · ') : null;
  const languages = dp?.languages?.length ? dp.languages : [];

  const modes = (dp?.consultation.modes?.length ? dp.consultation.modes : provider.consultModes) ?? [];
  const distance = distanceLabel(coords, provider);

  const registrationClaims = [
    'Identity verified',
    'Medical registration verified',
    ...(dp?.registration.council ? [`Registered with ${dp.registration.council}`] : []),
  ];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.white,
        border: `1px solid ${hovered ? T.teal + '40' : T.border}`,
        borderRadius: 16,
        padding: '16px',
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <DoctorAvatar name={provider.name} photoUrl={dp?.photoUrl ?? provider.logoUrl} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontFamily: T.font, fontSize: 15, fontWeight: 700, color: T.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {provider.name}
            </span>
            {provider.isVerified && (
              <span
                title={registrationClaims.join(' · ')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                  background: T.teal + '15', border: `1px solid ${T.teal}30`,
                  borderRadius: 20, padding: '2px 7px', cursor: 'help',
                }}
              >
                <IconVerified color={T.teal} />
                <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 700, color: T.teal, letterSpacing: '0.05em' }}>VERIFIED</span>
              </span>
            )}
          </div>

          {/* Never invent a specialty for a doctor who hasn't recorded one. */}
          <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 2 }}>
            {provider.specialties?.[0] || 'General Physician'}
          </div>

          <div style={{ marginTop: 5 }}>
            <RatingChip rating={provider.rating} count={provider.reviewCount} />
          </div>

          {/* Only a real issued 12-digit NeoHealthCard id — never one derived
              from the doctor's name. */}
          {formatNhcId(provider.neoHealthCardId) && (
            <div style={{ fontFamily: T.mono, fontSize: 10, letterSpacing: '0.03em', color: T.teal, marginTop: 3 }}>
              NHC · {formatNhcId(provider.neoHealthCardId)}
            </div>
          )}
        </div>

        <button
          onClick={onToggleSave}
          aria-label={isSaved ? 'Remove from saved doctors' : 'Save this doctor'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}
        >
          <IconHeart filled={isSaved} color={isSaved ? T.danger : T.textTer} />
        </button>
      </div>

      {/* Verification reads as the badge beside the name; these are the
          factual leftovers — a pending state, and the doctor's experience. */}
      {(!provider.isVerified || experienceLabel) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, marginBottom: 7 }}>
          {!provider.isVerified && <Chip label="Verification pending" color={T.warning} />}
          {experienceLabel && <Chip label={experienceLabel} color={T.textSec} />}
        </div>
      )}

      {degreesLine && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
          <span style={{ display: 'flex', flexShrink: 0 }}><IconSchool color={T.textSec} /></span>
          <span style={{
            fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {degreesLine}
          </span>
        </div>
      )}

      {/* Where they practise, and — only when both the patient and the
          directory have real coordinates — how far that is. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'flex', flexShrink: 0 }}><IconBuilding color={T.navy} /></span>
        <span style={{ fontFamily: T.font, fontSize: 11, color: T.text, fontWeight: 700 }}>
          {provider.city || 'Practice not stated'}
        </span>
        {distance && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: T.font, fontSize: 11, color: T.textSec, fontWeight: 600 }}>
            <IconLocation color={T.textTer} />
            {distance}
          </span>
        )}
      </div>

      {provider.openHours && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
          <span style={{ display: 'flex', flexShrink: 0 }}><IconClock color={T.textTer} /></span>
          <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec }}>{provider.openHours}</span>
        </div>
      )}

      {/* How this doctor actually consults, as the server derived it from the
          schedules they published. An empty list means nothing is known — and
          that is not the same as "in clinic only". */}
      {(modes.length > 0 || languages.length > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {modes.map((m) => {
            const meta = MODE_LABEL[m];
            return meta ? <ModePill key={m} label={meta.label} color={meta.color} /> : null;
          })}
          {languages.length > 0 && (
            <span style={{
              fontFamily: T.font, fontSize: 10, color: T.textSec,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
            }}>
              · {languages.join(' · ')}
            </span>
          )}
        </div>
      )}

      {consultFee != null && (
        <div style={{ fontFamily: T.font, fontSize: 11.5, color: T.textSec, marginTop: 9 }}>
          {feeLabel}{' '}
          <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>₹{consultFee}</span>
        </div>
      )}

      <button
        onClick={() => setAskOpen(true)}
        style={{
          width: '100%', marginTop: 10, padding: '11px 0', borderRadius: 10,
          background: T.teal + '1A', border: `1px solid ${T.teal}47`, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: T.font, fontSize: 12.5, fontWeight: 700, color: T.navy,
        }}
      >
        <IconChat color={T.navy} />
        Ask a quick question
      </button>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          onClick={onViewProfile}
          style={{
            flex: 1,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 600,
            color: T.navy,
            background: T.white,
            border: `1.5px solid ${T.border}`,
            borderRadius: 10,
            padding: '9px 0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            transition: 'border-color 0.12s ease',
          }}
        >
          View Profile
          <IconChevronRight color={T.navy} />
        </button>
        <button
          onClick={onBook}
          style={{
            flex: 1,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 700,
            color: T.white,
            background: `linear-gradient(135deg, ${T.teal} 0%, #009B9B 100%)`,
            border: 'none',
            borderRadius: 10,
            padding: '9px 0',
            cursor: 'pointer',
            boxShadow: `0 2px 8px ${T.teal}40`,
          }}
        >
          Book
        </button>
      </div>

      {askOpen && (
        <QuickQuestionsSheet provider={provider} onClose={() => setAskOpen(false)} onViewProfile={onViewProfile} />
      )}
    </div>
  );
}
